import React, { useEffect, useState } from "react";
import { Table, Button, Input, Space, Popconfirm, message, Col } from "antd";
import {
    SearchOutlined,
    ReloadOutlined,
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
} from "@ant-design/icons";
import DeliveryAddressServ from "../../service/DeliveryAddressesServ";
import AddDeliveryAddress from "../../component/AddDeliveryAddress";
import Breadcrumbs from "../../component/Breadcrumbs";
import { FilterBar } from "../../component/ui/FilterBar";
import SearchInput from "../../component/ui/SearchInput";
import { PrimaryButton, ResetButton } from "../../component/ui/ActionButtons";

const DeliveryAddressSetUp = () => {
    const [addresses, setAddresses] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [addAddressModal, setAddAddressModal] = useState(false);
    const [addressDetails, setAddressDetails] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [totalAddress, setTotalAddress] = useState(0);
    const [searchText, setSearchText] = useState("");
    const [loading, setLoading] = useState(false);

    const tenantId = localStorage.getItem("tenantId");

    const fetchAddresses = async (pageNumber = 1) => {
        try {
            setLoading(true);
            const params = {
                limit: pageSize,
                skip: (pageNumber - 1) * pageSize,
                tenantId: tenantId,
            };

            const response = await DeliveryAddressServ.index(params);
            const data = Array.isArray(response) ? response : [];

            setAddresses(data);
            setTotalAddress(response?.total || data.length);
        } catch (error) {
            console.error("Error fetching delivery addresses:", error);
            setAddresses([]);
            message.error("Failed to fetch delivery addresses");
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => setPage(newPage);
    const onShowSizeChange = (current, size) => {
        setPageSize(size);
        setPage(1);
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchAddresses(page);
        }, 400);
        return () => clearTimeout(timer);
    }, [page, pageSize, tenantId]);

    const handleAddAddressOpen = () => {
        setAddressDetails(null);
        setIsEditing(false);
        setAddAddressModal(true);
    };

    const handleEdit = (record) => {
        setAddressDetails(record);
        setIsEditing(true);
        setAddAddressModal(true);
    };

    const refreshAddress = async (isRefresh) => {
        setAddAddressModal(false);
        if (isRefresh) fetchAddresses(page);
    };

    const handleDelete = async (id) => {
        try {
            await DeliveryAddressServ.remove(id);
            message.success("Address removed successfully");
            fetchAddresses(page);
        } catch (error) {
            console.error("Error deleting address:", error);
            message.error("Failed to remove address");
        }
    };

    const filteredAddresses = (Array.isArray(addresses) ? addresses : []).filter(
        (a) =>
            a.addressLine?.toLowerCase().includes(searchText.toLowerCase()) ||
            a.name?.toLowerCase().includes(searchText.toLowerCase())
    );

    const columns = [
        { 
            title: "Address Name", 
            dataIndex: "name", 
            key: "name", 
            width: 150, 
            ellipsis: true,
            render: (name) => <span style={{ fontWeight: 600, color: '#1a1a2e' }}>{name}</span>,
        },
        { title: "Full Address", dataIndex: "addressLine", key: "addressLine", ellipsis: true },
        { title: "City", dataIndex: "city", key: "city", width: 120 },
        { title: "Pincode", dataIndex: "pincode", key: "pincode", width: 100 },
        { title: "Contact", dataIndex: "contactNumber", key: "contactNumber", width: 150 },
        {
            title: "Default",
            dataIndex: "isDefault",
            key: "isDefault",
            width: 80,
            render: (value) => (value ? <span style={{ color: "green" }}>Yes</span> : "No"),
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => {
                if (record.isShopAddress) {
                    return <span style={{ color: "#999", fontStyle: "italic" }}>Managed in Shop Setup</span>;
                }
                return (
                    <Space>
                        <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} type="default">
                            Edit
                        </Button>
                        <Popconfirm
                            title="Remove this address?"
                            onConfirm={() => handleDelete(record._id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button danger icon={<DeleteOutlined />}> Remove </Button>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    return (
        <div className="pcu-padding-24">
            <div className="pcu-margin-bottom-16">
                <Breadcrumbs />
            </div>

            <div className="main-content-card pcu-bg-white pcu-radius-16 pcu-shadow-sm pcu-overflow-hidden">
        <div className="pcu-padding-x-24 pcu-padding-top-24 pcu-padding-bottom-0 pcu-border-bottom">
          <h1 className="pcu-font-22 pcu-font-700 pcu-color-title pcu-margin-bottom-16">
            Delivery Address Setup
          </h1>
        </div>

        <div className="pcu-padding-24">
          <FilterBar>
            <Col xs={24} md={12}>
              <SearchInput
                label="Search"
                placeholder="Search by street or name..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </Col>
            <Col xs={24} md={12} style={{ textAlign: 'right' }}>
              <Space>
                <ResetButton
                  icon={<ReloadOutlined />}
                  onClick={() => setSearchText("")}
                  text="Reset"
                />
                <PrimaryButton
                  icon={<PlusOutlined />}
                  onClick={handleAddAddressOpen}
                  text="Create"
                />
              </Space>
            </Col>
          </FilterBar>
                    <Table
                        className="custom-table table-container"
                        dataSource={filteredAddresses}
                        columns={columns}
                        rowKey="_id"
                        loading={loading}
                        pagination={{
                            className: "custom-pagination",
                            current: page,
                            pageSize: pageSize,
                            total: totalAddress,
                            showSizeChanger: true,
                            onShowSizeChange: onShowSizeChange,
                            showTotal: (total) => `Total ${total} items`,
                            onChange: handlePageChange,
                            position: ["bottomRight"],
                        }}
                        bordered
                        scroll={{ x: "max-content", y: 400 }}
                    />

                    {addAddressModal && (
                        <AddDeliveryAddress
                            open={addAddressModal}
                            addressDetails={addressDetails}
                            isEditing={isEditing}
                            refreshAddress={refreshAddress}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default DeliveryAddressSetUp;
